import { useState, useEffect } from "react";
import ModalFrame from "../../components/modal/ModalFrame";
import { Dialog } from "@headlessui/react";
import * as api from "./apiService";

const ChevronIcon = ({ open = false }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={`h-5 w-5 transition-transform duration-200 ${
      open ? "rotate-180" : "rotate-0"
    }`}
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.25 8.29a.75.75 0 01-.02-1.08z"
      clipRule="evenodd"
    />
  </svg>
);

const JobInitialInspectionModal = ({
  isOpen,
  handleClose,
  startJob,
  jobDetails,
}) => {
  const [comment, setComment] = useState("");
  const [inspectionPerformedBy, setInspectionPerformedBy] = useState("");
  const [globalInspectionChecklist, setGlobalInspectionChecklist] = useState(
    []
  );
  const [showChecklist, setShowChecklist] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.getGlobalInspectionChecklist({
          inspection_type: "I",
        });
        const items = data.results
          .flatMap((item) => (item.item_description || "").split(/\r?\n/))
          .map((s) => s.trim())
          .filter((s) => s.length > 0);
        setGlobalInspectionChecklist(items);
      } catch {
        alert("Unable to fetch global inspection checklist.");
      }
    })();
  }, []);

  const handleNothingToReport = () => {
    if (inspectionPerformedBy.trim() === "") {
      alert("Please enter the name of the person performing the inspection.");
      return;
    }
    startJob(inspectionPerformedBy.trim());
  };

  const handlePreExistingConditionToReport = async () => {
    if (inspectionPerformedBy.trim() === "") {
      alert("Please enter the name of the person performing the inspection.");
      return;
    }
    if (comment.trim() === "") {
      alert("Please enter a comment to report pre-existing condition.");
      return;
    }
    try {
      const { data } = await api.createJobComment(jobDetails.id, {
        // If you want to persist who performed the inspection, you can prepend it:
        // comment: `Inspection performed by: ${inspectionPerformedBy}\n${comment}`,
        comment,
        isPublic: true,
        sendEmail: true,
      });

      setComment("");
      startJob(inspectionPerformedBy.trim(), data.id);
    } catch {
      alert("Unable to create comment.");
    }
  };

  return (
    <ModalFrame isModalOpen={isOpen}>
      <div className="px-6 pt-5 pb-4">
        <Dialog.Title
          as="h3"
          className="text-2xl font-semibold text-gray-900 text-center"
        >
          Initial Inspection / Walkthrough
        </Dialog.Title>

        {/* Acknowledgment */}
        <div className="mt-5 rounded-lg border border-gray-200 bg-white">
          <div className="px-4 py-3 border-b border-gray-200">
            <h4 className="text-sm font-semibold tracking-wide text-gray-700 uppercase">
              Acknowledgment
            </h4>
          </div>
          <div className="px-4 py-4 text-gray-700 leading-relaxed">
            Initial inspection completed and all pre-existing issues documented
            in LiveTakeoff with photos and notes (if any).
          </div>
        </div>

        {/* Checklist (collapsible + fixed height scroll) */}
        <div className="mt-4 rounded-lg border border-gray-200 bg-white">
          <button
            type="button"
            onClick={() => setShowChecklist((v) => !v)}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-sky-500 rounded-t-lg"
            aria-expanded={showChecklist}
            aria-controls="inspection-checklist"
          >
            <span className="text-sm font-semibold tracking-wide text-gray-700 uppercase">
              Review Checklist
            </span>
            <ChevronIcon open={showChecklist} />
          </button>

          {showChecklist && (
            <div id="inspection-checklist" className="px-4 pt-2 pb-4">
              <div className="max-h-56 overflow-y-auto pr-1">
                {globalInspectionChecklist.length === 0 ? (
                  <div className="text-sm text-gray-500">No items found.</div>
                ) : (
                  <ul className="list-none pl-1 space-y-2 text-gray-700">
                    {globalInspectionChecklist.map((item, idx) => (
                      <li key={idx} className="text-sm leading-6">
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Performed By (NEW) */}
        <div className="mt-4 rounded-lg border border-gray-200 bg-white">
          <div className="px-4 py-3 border-b border-gray-200">
            <h4 className="text-sm font-semibold tracking-wide text-gray-700 uppercase">
              Inspection Performed By
            </h4>
          </div>
          <div className="px-4 py-4">
            <label
              htmlFor="performed-by"
              className="block text-xs font-medium text-gray-600 mb-1"
            >
              Full name (required)
            </label>
            <input
              id="performed-by"
              type="text"
              value={inspectionPerformedBy}
              onChange={(e) => setInspectionPerformedBy(e.target.value)}
              placeholder="e.g., John Smith"
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 text-sm"
              autoComplete="name"
            />
          </div>
        </div>

        {/* Comment box */}
        <div className="mt-4 rounded-lg border border-gray-200 bg-white">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h4 className="text-sm font-semibold tracking-wide text-gray-700 uppercase">
              Report Any Issues (Optional)
            </h4>
            <span className="text-xs text-gray-400">Add notes if needed</span>
          </div>
          <div className="px-4 py-4">
            <textarea
              id="comment"
              name="comment"
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add an explanation..."
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 text-sm"
            />
          </div>
        </div>

        {/* Footer buttons (stacked) */}
        <div className="mt-6 grid grid-cols-1 gap-3">
          <button
            type="button"
            onClick={handleClose}
            className="inline-flex w-full items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-sky-500"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleNothingToReport}
            className="inline-flex w-full items-center justify-center rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500"
          >
            Nothing to Report
          </button>
          <button
            type="button"
            onClick={handlePreExistingConditionToReport}
            className="inline-flex w-full items-center justify-center rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500"
          >
            Report Pre-existing Condition
          </button>
        </div>
      </div>
    </ModalFrame>
  );
};

export default JobInitialInspectionModal;
